<template>
  <div class="setup" :class="{ 'setup--with-navigation': appNavigation }">
    <div class="deco deco--star1" aria-hidden="true"></div>
    <div class="deco deco--star2" aria-hidden="true"></div>
    <div class="deco deco--star3" aria-hidden="true"></div>

    <div class="college-header">
      <div class="logos-row">
        <img class="college-logo-img" src="/assets/pomesto-logo.svg" alt="поместо" />
      </div>
      <div class="college-badge">Делай своё. Строй своё.</div>
    </div>

    <div class="setup__wrap">
      <!-- Sticky preview panel -->
      <div class="setup__sticky-panel" aria-label="Предпросмотр персонажа">
        <div class="setup__sticky-inner">
          <div class="setup__preview-label">{{ userName || 'Персонаж' }}</div>
          <CharacterPreview :appearance="appearance" class="setup__preview" />
          <div class="setup__preview-city" v-if="userCity">Город: {{ userCity }}</div>
          <button
            class="primary primary--compact"
            :disabled="!isValid || saving"
            @click="submit"
            type="button"
          >
            {{ saving ? 'Сохранение…' : mode === 'profile' ? 'Сохранить и вернуться' : 'Войти в поместо' }}
          </button>
          <div class="setup__auth-link setup__auth-link--desktop">
            <template v-if="account">
              <span class="setup__auth-saved">Профиль сохранён</span>
              <span class="setup__auth-link-hint">{{ account.displayName || account.email || 'VK ID' }}</span>
            </template>
            <button v-else class="setup__auth-link-text" type="button" @click="$emit('openAuth')">
              Войти или зарегистрироваться
            </button>
            <span v-if="!account" class="setup__auth-link-hint">для сохранения вашей учётной записи</span>
          </div>
        </div>
      </div>

      <!-- Form panel -->
      <div class="setup__form">
        <div class="setup__hero">
          <h1 class="setup__title">{{ mode === 'profile' ? 'Твой персонаж' : 'Создай персонажа' }}</h1>
          <p class="setup__subtitle">{{ mode === 'profile' ? 'Здесь собраны анкета, внешний вид и настройки аккаунта.' : 'Пора на своё место. Настрой образ и выбери, чем займёшься.' }}</p>
        </div>

        <div class="card">
          <label class="field">
            <span class="field__label">Как тебя зовут?</span>
            <input
              v-model.trim="userName"
              class="field__input"
              placeholder="Твоё имя"
              maxlength="20"
            />
          </label>
          <label class="field">
            <span class="field__label">Город <small>(необязательно)</small></span>
            <input
              v-model.trim="userCity"
              class="field__input"
              placeholder="Например, Москва"
              maxlength="24"
            />
          </label>
          <p class="field__privacy">Город будет виден другим участникам комнаты рядом с именем. Можно оставить поле пустым.</p>
        </div>

        <section class="notification-setting" aria-labelledby="notification-setting-title">
          <span class="notification-setting__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9Z" />
              <path d="M10 21h4" />
            </svg>
          </span>
          <span class="notification-setting__copy">
            <strong id="notification-setting-title">Уведомления</strong>
            <small>{{ notificationHint }}</small>
          </span>
          <button
            class="notification-setting__button"
            :class="{ 'notification-setting__button--enabled': notificationState === 'granted' }"
            :disabled="notificationState !== 'default' || notificationPending"
            type="button"
            @click="requestNotifications"
          >
            {{ notificationButtonLabel }}
          </button>
        </section>

        <button
          class="primary primary--early-mobile"
          :disabled="!isValid || saving"
          @click="submit"
          type="button"
        >
          {{ saving ? 'Сохранение…' : mode === 'profile' ? 'Сохранить и вернуться' : 'Выбрать комнату' }}
        </button>

        <div class="setup__auth-link setup__auth-link--mobile">
          <template v-if="account">
            <span class="setup__auth-saved">Профиль сохранён</span>
            <span class="setup__auth-link-hint">{{ account.displayName || account.email || 'VK ID' }}</span>
          </template>
          <button v-else class="setup__auth-link-text" type="button" @click="$emit('openAuth')">
            Войти или зарегистрироваться
          </button>
          <span v-if="!account" class="setup__auth-link-hint">для сохранения вашей учётной записи</span>
        </div>

        <p class="setup__optional-title">Остальные настройки необязательны</p>

        <section class="card">
          <h3 class="card__title">Пол</h3>
          <div class="chips">
            <button
              class="chip"
              :class="{ 'chip--active': gender === 'female' }"
              :aria-pressed="gender === 'female'"
              @click="setGender('female')"
              type="button"
            >
              Девушка
            </button>
            <button
              class="chip"
              :class="{ 'chip--active': gender === 'male' }"
              :aria-pressed="gender === 'male'"
              @click="setGender('male')"
              type="button"
            >
              Парень
            </button>
          </div>
        </section>

        <section class="card">
          <h3 class="card__title">Персонаж</h3>
          <div class="chips chips--grid">
            <button
              v-for="c in characterOptions"
              :key="c.key"
              class="chip"
              :class="{ 'chip--active': appearance.characterKey === c.key }"
              :aria-pressed="appearance.characterKey === c.key"
              @click="appearance.characterKey = c.key"
              type="button"
            >
              <span>{{ c.title }}</span>
            </button>
          </div>
        </section>

        <section class="card">
          <h3 class="card__title">Цвет кожи</h3>
          <div class="swatches">
            <button
              v-for="(color, index) in SKIN_COLORS"
              :key="color"
              class="swatch"
              :style="{ background: color }"
              :class="{ 'swatch--active': appearance.skinColor === color }"
              :aria-label="`Цвет кожи, вариант ${index + 1}`"
              :aria-pressed="appearance.skinColor === color"
              @click="appearance.skinColor = color"
              type="button"
            ></button>
          </div>
        </section>

        <section class="card">
          <h3 class="card__title">Причёска</h3>
          <div class="chips chips--grid">
            <button
              v-for="h in hairOptions"
              :key="h.key"
              class="chip"
              :class="{ 'chip--active': appearance.hairKey === h.key }"
              :aria-pressed="appearance.hairKey === h.key"
              @click="appearance.hairKey = h.key"
              type="button"
            >
              <span>{{ h.title }}</span>
            </button>
          </div>
          <p class="field-label">Цвет волос</p>
          <div class="swatches">
            <button
              v-for="(color, index) in HAIR_COLORS"
              :key="color"
              class="swatch"
              :style="{ background: color }"
              :class="{ 'swatch--active': appearance.hairColor === color }"
              :aria-label="`Цвет волос, вариант ${index + 1}`"
              :aria-pressed="appearance.hairColor === color"
              @click="appearance.hairColor = color"
              type="button"
            ></button>
          </div>
        </section>

        <section class="card">
          <h3 class="card__title">Одежда</h3>
          <div class="chips chips--grid">
            <button
              v-for="c in clothesOptions"
              :key="c.key"
              class="chip"
              :class="{ 'chip--active': appearance.clothesKey === c.key }"
              :aria-pressed="appearance.clothesKey === c.key"
              @click="appearance.clothesKey = c.key"
              type="button"
            >
              <span>{{ c.title }}</span>
            </button>
          </div>
          <div class="swatches">
            <button
              v-for="(color, index) in CLOTHES_COLORS"
              :key="color"
              class="swatch"
              :style="{ background: color }"
              :class="{ 'swatch--active': appearance.clothesColor === color }"
              :aria-label="`Цвет одежды, вариант ${index + 1}`"
              :aria-pressed="appearance.clothesColor === color"
              @click="appearance.clothesColor = color"
              type="button"
            ></button>
          </div>
        </section>

        <section v-if="mode === 'profile' && account" class="account-settings">
          <div class="account-settings__identity">
            <img v-if="account.avatarUrl" :src="account.avatarUrl" alt="" referrerpolicy="no-referrer" />
            <span v-else aria-hidden="true">{{ (account.displayName || account.email || '?').slice(0, 1) }}</span>
            <div><strong>{{ account.displayName || account.email || 'Учётная запись' }}</strong><small>Профиль и прогресс сохранены</small></div>
          </div>
          <button type="button" :disabled="logoutPending" @click="logout">{{ logoutPending ? 'Выходим…' : 'Выйти из аккаунта' }}</button>
          <p v-if="logoutError" role="alert">{{ logoutError }}</p>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from "vue"
import CharacterPreview from "./CharacterPreview.vue"
import {
  SKIN_COLORS,
  HAIR_COLORS,
  CLOTHES_COLORS,
  charactersFor,
  hairstylesFor,
  clothesFor,
} from "../shared/presets"
import type { PlayerAppearance, StudyType } from "../shared/types"
import type { AuthAccount } from "../api/auth"
import { logoutRoute } from "../api/auth"
import {
  enableBrowserNotifications,
  getBrowserNotificationState,
  prepareBrowserNotifications,
  type BrowserNotificationState,
} from "../shared/browserNotifications"

const props = defineProps<{
  mode?: "setup" | "profile"
  appNavigation?: boolean
  account?: AuthAccount | null
  initial?: {
    userName?: string
    userCity?: string
    appearance?: PlayerAppearance
    studyType?: StudyType | null
    studyValue?: string
  }
}>()

const emit = defineEmits<{
  done: [payload: { userName: string; userCity: string; appearance: PlayerAppearance; studyType: StudyType | null; studyValue: string }]
  openAuth: []
  loggedOut: []
}>()

const saving = ref(false)
const logoutPending = ref(false)
const logoutError = ref("")
const notificationState = ref<BrowserNotificationState>(getBrowserNotificationState())
const notificationPending = ref(false)

const notificationHint = computed(() => {
  if (notificationState.value === "granted") return "Сообщим, когда закончится фокус."
  if (notificationState.value === "denied") return "Разрешить можно в настройках браузера."
  if (notificationState.value === "unsupported") return "Этот браузер пока не поддерживает уведомления."
  return "Сообщим, когда закончится фокус."
})

const notificationButtonLabel = computed(() => {
  if (notificationPending.value) return "Включаем…"
  if (notificationState.value === "granted") return "Включены"
  if (notificationState.value === "denied") return "Заблокированы"
  if (notificationState.value === "unsupported") return "Недоступны"
  return "Включить"
})

onMounted(async () => {
  notificationState.value = getBrowserNotificationState()
  await prepareBrowserNotifications()
})

async function requestNotifications() {
  notificationPending.value = true
  try {
    notificationState.value = await enableBrowserNotifications()
  } finally {
    notificationPending.value = false
  }
}

async function logout() {
  if (logoutPending.value) return
  logoutPending.value = true
  logoutError.value = ""
  try {
    await logoutRoute.run(ctx)
    emit("loggedOut")
  } catch {
    logoutError.value = "Не удалось выйти. Проверь соединение и попробуй ещё раз."
  } finally {
    logoutPending.value = false
  }
}

const userName = ref(props.initial?.userName || "")
const userCity = ref(props.initial?.userCity || "")

const studyType = ref<StudyType | null>(props.initial?.studyType || null)
const studyValue = ref<string>(props.initial?.studyValue || "")

const gender = ref<"male" | "female">(props.initial?.appearance?.gender || "female")

const appearance = reactive<PlayerAppearance>(
  props.initial?.appearance || {
    gender: "female",
    characterKey: charactersFor("female")[0]!.key,
    hairKey: hairstylesFor("female")[0]!.key,
    clothesKey: clothesFor("female")[0]!.key,
    skinColor: SKIN_COLORS[1]!,
    hairColor: HAIR_COLORS[0]!,
    clothesColor: CLOTHES_COLORS[3]!,
  },
)

const characterOptions = computed(() => charactersFor(gender.value))
const hairOptions = computed(() => hairstylesFor(gender.value))
const clothesOptions = computed(() => clothesFor(gender.value))

function setGender(g: "male" | "female") {
  gender.value = g
  appearance.gender = g
  if (!characterOptions.value.find((c) => c.key === appearance.characterKey)) {
    appearance.characterKey = characterOptions.value[0]!.key
  }
  if (!hairOptions.value.find((h) => h.key === appearance.hairKey)) {
    appearance.hairKey = hairOptions.value[0]!.key
  }
  if (!clothesOptions.value.find((c) => c.key === appearance.clothesKey)) {
    appearance.clothesKey = clothesOptions.value[0]!.key
  }
}

watch(gender, (g) => {
  appearance.gender = g
})

watch(
  () => props.initial,
  (init) => {
    if (!init) return
    if (init.userName) userName.value = init.userName
    if (init.userCity) userCity.value = init.userCity
    if (init.studyType !== undefined) studyType.value = init.studyType ?? null
    if (init.studyValue !== undefined) studyValue.value = init.studyValue ?? ""
    if (init.appearance) {
      const a = init.appearance
      gender.value = a.gender || "female"
      appearance.gender = a.gender || "female"
      appearance.characterKey = a.characterKey || ""
      appearance.hairKey = a.hairKey || ""
      appearance.clothesKey = a.clothesKey || ""
      appearance.skinColor = a.skinColor || ""
      appearance.hairColor = a.hairColor || ""
      appearance.clothesColor = a.clothesColor || ""
    }
  },
  { deep: true }
)

if (!characterOptions.value.find((c) => c.key === appearance.characterKey)) {
  appearance.characterKey = characterOptions.value[0]!.key
}
if (!hairOptions.value.find((h) => h.key === appearance.hairKey)) {
  appearance.hairKey = hairOptions.value[0]!.key
}
if (!clothesOptions.value.find((c) => c.key === appearance.clothesKey)) {
  appearance.clothesKey = clothesOptions.value[0]!.key
}

const isValid = computed(() => userName.value.length >= 2)

async function submit() {
  if (!isValid.value) return
  saving.value = true
  saving.value = false
  emit("done", {
    userName: userName.value,
    userCity: userCity.value,
    appearance: { ...appearance },
    studyType: null,
    studyValue: "",
  })
}
</script>

<style scoped>
/* ── Base ── */
.setup {
  min-height: 100vh;
  background: #F6F2E9;
  position: relative;
  font-family: "Inter", system-ui, -apple-system, sans-serif;
  color: #292B33;
  padding-bottom: 48px;
}

/* ── Decorative stars ── */
.deco {
  position: fixed;
  width: 74px;
  height: 74px;
  border: 14px solid rgba(98, 88, 217, 0.12);
  border-right: 0;
  border-bottom: 0;
  border-radius: 18px 0 0;
  pointer-events: none;
  user-select: none;
  opacity: 1;
  z-index: 0;
}
.deco--star1 { top: 18%; left: 4%; }
.deco--star2 { top: 34%; right: 4%; rotate: 180deg; }
.deco--star3 { bottom: 12%; left: 7%; scale: 0.62; }

/* ── College header ── */
.college-header {
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 24px;
  background: rgba(255, 253, 248, 0.94);
  border-bottom: 1px solid rgba(41, 43, 51, 0.1);
  backdrop-filter: blur(14px);
}
.college-logo-img {
  height: 32px;
  width: auto;
  display: block;
}
.logos-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.logo-divider {
  color: #6258D9;
  font-size: 20px;
  font-weight: 300;
  opacity: 0.4;
}
.college-badge {
  background: rgba(242, 184, 75, 0.2);
  color: #292B33;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
  padding: 5px 12px;
  border-radius: 999px;
}

/* ── Layout ── */
.setup__wrap {
  display: flex;
  align-items: flex-start;
  max-width: 960px;
  margin: 0 auto;
  padding: 28px 16px 40px;
  gap: 24px;
  position: relative;
  z-index: 1;
}

/* ── Sticky preview panel ── */
.setup__sticky-panel {
  position: sticky;
  top: 72px;
  width: 260px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.setup__sticky-inner {
  background: #FFFDF8;
  border: 1px solid rgba(41, 43, 51, 0.09);
  border-radius: 24px;
  padding: 20px 16px;
  box-shadow: 0 18px 50px rgba(41, 43, 51, 0.1);
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.setup__sticky-back {
  width: 100%;
}
.setup__preview-label {
  font-size: 18px;
  font-weight: 700;
  color: #292B33;
  text-align: center;
  min-height: 24px;
}
.setup__preview-city {
  font-size: 13px;
  font-weight: 600;
  color: #666873;
  text-align: center;
}
.setup__preview {
  width: 100%;
}

/* ── Form panel ── */
.setup__form {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.setup__topbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 4px;
}
.back-btn--mobile {
  display: none;
}
.back-btn {
  padding: 8px 18px;
  border-radius: 999px;
  border: none;
  background: #fff;
  color: #6258D9;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(98, 88, 217, 0.15);
  flex-shrink: 0;
}
.setup__mode-label {
  font-size: 20px;
  font-weight: 700;
  color: #292B33;
}

/* ── Hero block ── */
.setup__hero {
  padding: 8px 0 4px;
}
.setup__title {
  font-size: 44px;
  margin: 0 0 4px;
  font-weight: 700;
  color: #292B33;
  letter-spacing: -1.2px;
  text-transform: none;
  line-height: 1;
}
.setup__subtitle {
  margin: 0;
  color: #666873;
  font-weight: 500;
  font-size: 15px;
}

/* ── Cards ── */
.card {
  background: #FFFDF8;
  border: 1px solid rgba(41, 43, 51, 0.08);
  border-radius: 20px;
  padding: 16px 18px;
  box-shadow: 0 10px 30px rgba(41, 43, 51, 0.06);
}
.card__title {
  margin: 0 0 12px;
  font-size: 13px;
  font-weight: 700;
  color: #292B33;
  text-transform: none;
  letter-spacing: 0;
}
.field-label {
  margin: 14px 0 2px;
  color: #666873;
  font-size: 12px;
  font-weight: 700;
}

/* ── Fields ── */
.field {
  display: block;
  margin-bottom: 10px;
}
.field:last-child {
  margin-bottom: 0;
}
.field__label {
  display: block;
  font-size: 13px;
  font-weight: 700;
  color: #666873;
  margin-bottom: 6px;
}
.field__input {
  width: 100%;
  padding: 12px 16px;
  border-radius: 14px;
  border: 2px solid #E8E5F4;
  background: #FBF9F4;
  font-size: 16px;
  font-weight: 600;
  color: #292B33;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.2s;
  font-family: "Inter", system-ui, sans-serif;
}
.field__input:focus {
  border-color: #6258D9;
  background: #fff;
}

/* ── Chips ── */
.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.chips--grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
  gap: 8px;
}
.chip {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  border-radius: 14px;
  border: 2px solid #E8E5F4;
  background: #FBF9F4;
  color: #292B33;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.12s, border-color 0.15s, background 0.15s, color 0.15s;
  font-family: "Inter", system-ui, sans-serif;
}
.chip:hover {
  transform: translateY(-1px);
  border-color: #6258D9;
}
.chip--active {
  background: #6258D9;
  border-color: #6258D9;
  color: #fff;
  box-shadow: 0 4px 14px rgba(98, 88, 217, 0.3);
}
.chip__emoji {
  font-size: 18px;
}

/* ── Swatches ── */
.swatches {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}
.swatch {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 3px solid #fff;
  outline: 2px solid transparent;
  cursor: pointer;
  transition: transform 0.15s, outline-color 0.15s;
}
.swatch:hover {
  transform: scale(1.08);
}
.swatch--active {
  outline-color: #6258D9;
  transform: scale(1.12);
}

/* ── Primary button ── */
.primary {
  padding: 16px 24px;
  border-radius: 16px;
  background: #6258D9;
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  box-shadow: 0 10px 24px rgba(98, 88, 217, 0.22);
  transition: transform 0.15s, box-shadow 0.15s;
  width: 100%;
  font-family: "Inter", system-ui, sans-serif;
  text-transform: none;
  letter-spacing: 0;
}
.primary:hover:not([disabled]) {
  transform: translateY(-2px);
  box-shadow: 0 14px 30px rgba(98, 88, 217, 0.28);
}
.primary[disabled] {
  opacity: 0.45;
  cursor: not-allowed;
}
.primary--mobile {
  display: none;
}
.primary--early-mobile { display: none; }
.field__privacy {
  margin: -2px 0 0;
  color: #666873;
  font-size: 12px;
  line-height: 1.45;
}
.field__label small, .card__title small { color: #666873; font-size: .78em; font-weight: 600; }
.setup__optional-title { display: none; margin: 4px 2px -2px; color: #666873; font-size: 13px; font-weight: 600; }
.optional-details summary { cursor: pointer; list-style-position: outside; }
.optional-details__content { margin-top: 16px; }

/* ── Browser notifications ── */
.notification-setting {
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  min-height: 48px;
  padding: 10px 12px;
  border: 1px solid rgba(98, 88, 217, 0.16);
  border-radius: 16px;
  background: rgba(255, 253, 248, 0.78);
}
.notification-setting__icon {
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  color: #6258D9;
  border-radius: 12px;
  background: rgba(98, 88, 217, 0.1);
}
.notification-setting__icon svg {
  width: 21px;
  height: 21px;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.notification-setting__copy {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 2px;
}
.notification-setting__copy strong {
  color: #292B33;
  font-size: 13px;
  line-height: 1.2;
}
.notification-setting__copy small {
  color: #666873;
  font-size: 11px;
  line-height: 1.3;
}
.notification-setting__button {
  min-height: 36px;
  padding: 8px 12px;
  border: 0;
  border-radius: 11px;
  color: #fff;
  background: #6258D9;
  font: 700 12px/1 "Inter", system-ui, sans-serif;
  cursor: pointer;
}
.notification-setting__button:disabled {
  cursor: default;
  color: #666873;
  background: #E8E5F4;
}
.notification-setting__button--enabled:disabled {
  color: #4F7B3D;
  background: rgba(135, 166, 106, 0.18);
}

/* ── Auth link ── */
.setup__auth-link {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
  text-align: center;
}
.setup__auth-link--mobile { display: none; }
.setup__auth-link-text {
  padding: 0;
  border: 0;
  background: transparent;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  color: #6258D9;
  text-decoration: underline;
  cursor: pointer;
  transition: color 0.15s;
}
.setup__auth-saved { color: #4F7B3D; font-size: 13px; font-weight: 800; }
.setup__auth-link-text:hover {
  color: #6258D9;
}
.setup__auth-link-hint {
  font-size: 11px;
  font-weight: 600;
  color: #666873;
}
.account-settings{display:grid;grid-template-columns:minmax(0,1fr) auto;align-items:center;gap:14px;margin-top:8px;padding:18px;border:1px solid rgba(41,43,51,.1);border-radius:20px;background:#fffdf8;box-shadow:0 10px 30px rgba(41,43,51,.05)}.account-settings__identity{display:flex;min-width:0;align-items:center;gap:12px}.account-settings__identity>img,.account-settings__identity>span{width:44px;height:44px;flex:0 0 44px;border-radius:14px;object-fit:cover}.account-settings__identity>span{display:grid;place-items:center;background:#eeeaf8;color:#6258d9;font-size:18px;font-weight:800}.account-settings__identity div{min-width:0}.account-settings__identity strong,.account-settings__identity small{display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.account-settings__identity strong{font-size:13px}.account-settings__identity small{margin-top:3px;color:#777985;font-size:10px}.account-settings>button{min-height:42px;padding:0 15px;border:1px solid rgba(41,43,51,.14);border-radius:13px;background:#fff;color:#555862;font:750 12px/1 Inter,system-ui,sans-serif;cursor:pointer}.account-settings>button:disabled{opacity:.55}.account-settings>p{grid-column:1/-1;margin:0;color:#9b3526;font-size:11px;font-weight:650}

@media (min-width: 681px) {
  .setup--with-navigation > .college-header { display: none; }
}

/* ── Study tabs ── */
.study-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}
.study-tab {
  flex: 1;
  padding: 10px 8px;
  border-radius: 14px;
  border: 2px solid #E8E5F4;
  background: #FBF9F4;
  color: #292B33;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;
  font-family: "Inter", system-ui, sans-serif;
}
.study-tab:hover {
  border-color: #6258D9;
}
.study-tab--active {
  background: #6258D9;
  border-color: #6258D9;
  color: #fff;
  box-shadow: 0 4px 14px rgba(98, 88, 217, 0.3);
}
.study-field {
  margin-top: 4px;
}
.field__select {
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='12' height='8' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%236258D9' stroke-width='2' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  padding-right: 36px;
  cursor: pointer;
}

/* ── Mobile ── */
@media (max-width: 680px) {
  .college-header {
    position: sticky;
    top: 0;
  }
  .setup__wrap {
    flex-direction: column;
    padding: 16px 12px 32px;
  }
  .setup__sticky-panel {
    position: sticky;
    top: 60px;
    width: 100%;
    z-index: 50;
  }
  .setup__sticky-inner {
    flex-direction: row;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
  }
  .setup__preview {
    width: 100px;
    height: 120px;
    flex-shrink: 0;
  }
  .setup__sticky-inner > .primary--compact {
    display: none;
  }
  .setup__auth-link--desktop { display: none; }
  .setup__auth-link--mobile { display: flex; margin: -2px 0 2px; }
  .setup__sticky-inner .setup__preview-label {
    font-size: 15px;
  }
  .setup__sticky-back {
    display: none;
  }
  .back-btn--mobile {
    display: inline-flex;
  }
  .primary--mobile {
    display: block;
    margin-top: 8px;
    font-size: 18px;
  }
  .primary--early-mobile { display: block; margin-top: -4px; }
  .setup__optional-title { display: block; }
  .notification-setting { grid-template-columns: 36px minmax(0, 1fr) auto; padding: 9px 10px; gap: 8px; }
  .notification-setting__icon { width: 36px; height: 36px; }
  .notification-setting__button { padding-inline: 10px; }
  .chip, .study-tab { min-height: 44px; }
  .setup__title {
    font-size: 32px;
  }
  .account-settings{grid-template-columns:1fr}.account-settings>button{width:100%}
  .deco { display: none; }

  /* В профиле примерка работает как нижний док: образ остаётся видимым,
     а длинный список настроек прокручивается независимо над ним. */
  .setup.setup--with-navigation {
    padding-bottom: calc(188px + env(safe-area-inset-bottom));
  }
  .setup--with-navigation .setup__sticky-panel {
    position: fixed;
    z-index: 6000;
    inset: auto 0 0;
    width: auto;
    padding: 8px 12px max(8px, env(safe-area-inset-bottom));
    align-items: stretch;
    border-top: 1px solid rgba(41, 43, 51, 0.12);
    background: rgba(255, 253, 248, 0.97);
    box-shadow: 0 -10px 28px rgba(41, 43, 51, 0.12);
    backdrop-filter: blur(14px);
  }
  .setup--with-navigation .setup__sticky-inner {
    width: min(100%, 560px);
    min-height: 156px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 116px minmax(0, 1fr);
    grid-template-rows: minmax(24px, auto) minmax(0, 1fr) 48px;
    align-items: center;
    column-gap: 14px;
    row-gap: 4px;
    padding: 0;
    border: 0;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
  }
  .setup--with-navigation .setup__preview {
    grid-column: 1;
    grid-row: 1 / 4;
    width: 116px;
    height: 156px;
    border-radius: 18px;
  }
  .setup--with-navigation .setup__sticky-inner .setup__preview-label {
    grid-column: 2;
    grid-row: 1;
    align-self: end;
    max-width: 100%;
    overflow: hidden;
    color: #292B33;
    font-size: 15px;
    text-align: left;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .setup--with-navigation .setup__preview-city {
    grid-column: 2;
    grid-row: 2;
    align-self: start;
    max-width: 100%;
    overflow: hidden;
    text-align: left;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .setup--with-navigation .setup__sticky-inner > .primary--compact {
    grid-column: 2;
    grid-row: 3;
    display: block;
    min-height: 48px;
    padding: 10px 14px;
  }
  .setup--with-navigation .primary--early-mobile {
    display: none;
  }
}
</style>
